import { useState, useEffect, useMemo } from 'react';
import { Inversion } from '../../../shared/types';
import { InversionMockRepository } from '../services/inversion-mock.repository';
import { useAuth } from '../../auth/hooks/useAuth';
import { LoggerService } from '../../../shared/services/logger.service';

export const useInversionesMock = () => {
  const [inversiones, setInversiones] = useState<Inversion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const repository = useMemo(() => new InversionMockRepository(), []);

  useEffect(() => {
    if (!user) {
      setInversiones([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    LoggerService.debug('Subscribing to mock inversiones', 'useInversionesMock', { userId: user.uid });

    const unsubscribe = repository.subscribeToInversiones(user.uid, (inversiones) => {
      setInversiones(inversiones);
      setLoading(false);
      LoggerService.debug('Mock inversiones updated', 'useInversionesMock', { count: inversiones.length });
    });

    return () => unsubscribe();
  }, [user, repository]);

  const createInversion = async (inversion: Omit<Inversion, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.create(user.uid, inversion);
      LoggerService.logUserAction('mock_inversion_created', 'useInversionesMock', { inversion });
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      LoggerService.error('Mock inversion creation failed', 'useInversionesMock', { error });
      throw err;
    }
  };

  const updateInversion = async (id: string, inversion: Partial<Omit<Inversion, 'id' | 'createdAt' | 'updatedAt'>>) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.update(user.uid, id, inversion);
      LoggerService.logUserAction('mock_inversion_updated', 'useInversionesMock', { id, inversion });
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      LoggerService.error('Mock inversion update failed', 'useInversionesMock', { error });
      throw err;
    }
  };

  const deleteInversion = async (id: string) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.delete(user.uid, id);
      LoggerService.logUserAction('mock_inversion_deleted', 'useInversionesMock', { id });
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      LoggerService.error('Mock inversion deletion failed', 'useInversionesMock', { error });
      throw err;
    }
  };

  // Business logic methods
  const getInversionesByType = async (tipo: string): Promise<Inversion[]> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      return await repository.getByType(user.uid, tipo);
    } catch (err: unknown) {
      const error = err as { message: string };
      LoggerService.error('Mock get by type failed', 'useInversionesMock', { error });
      throw new Error(error.message);
    }
  };

  const getInversionesByDateRange = async (startDate: Date, endDate: Date): Promise<Inversion[]> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      return await repository.getByDateRange(user.uid, startDate, endDate);
    } catch (err: unknown) {
      const error = err as { message: string };
      LoggerService.error('Mock get by date range failed', 'useInversionesMock', { error });
      throw new Error(error.message);
    }
  };

  const getTotalInversiones = (): number => {
    return inversiones.reduce((total, inversion) => total + inversion.monto, 0);
  };

  const getInversionesByMonth = (year: number, month: number): Inversion[] => {
    return inversiones.filter(inversion => {
      const fecha = inversion.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
  };

  const getInversionesByYear = (year: number): Inversion[] => {
    return inversiones.filter(inversion => inversion.fecha.getFullYear() === year);
  };

  const getTypes = (): string[] => {
    const types = new Set(inversiones.map(inversion => inversion.tipo));
    return Array.from(types).sort();
  };

  const getTypeTotals = (): { [tipo: string]: number } => {
    const totals: { [tipo: string]: number } = {};
    inversiones.forEach(inversion => {
      totals[inversion.tipo] = (totals[inversion.tipo] || 0) + inversion.monto;
    });
    return totals;
  };

  return {
    inversiones,
    loading,
    error,
    createInversion,
    updateInversion,
    deleteInversion,
    getInversionesByType,
    getInversionesByDateRange,
    getTotalInversiones,
    getInversionesByMonth,
    getInversionesByYear,
    getTypes,
    getTypeTotals,
  };
};
